import {
  FC, memo, useState, useCallback,
} from 'react';
import { Modal, Button, message } from 'antd';
import { OrderListItem } from 'entities/order';
import { FileUpload } from 'shared/ui/FileUpload';
import { useGlobalSpinner } from 'shared/hooks';

interface CompleteOrderModalProps {
  order: OrderListItem | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reportFileUuid: string) => Promise<void>;
}

export const CompleteOrderModal: FC<CompleteOrderModalProps> = memo(({
  order,
  isOpen,
  onClose,
  onConfirm,
}) => {
  const [uploadedFileUuid, setUploadedFileUuid] = useState<string | null>(null);
  const { showSpinner, hideSpinner } = useGlobalSpinner();

  const handleFileUploaded = useCallback((uuid: string) => {
    setUploadedFileUuid(uuid);
  }, []);

  const handleFileRemoved = useCallback(() => {
    setUploadedFileUuid(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!uploadedFileUuid) {
      message.error('Пожалуйста, приложите файл отчета');
      return;
    }

    try {
      showSpinner('Завершение заявки...');
      await onConfirm(uploadedFileUuid);
      setUploadedFileUuid(null);
      onClose();
      message.success('Заявка успешно завершена');
    } catch (error) {
      message.error('Ошибка при завершении заявки');
    } finally {
      hideSpinner();
    }
  }, [uploadedFileUuid, onConfirm, onClose, showSpinner, hideSpinner]);

  const handleCancel = useCallback(() => {
    setUploadedFileUuid(null);
    onClose();
  }, [onClose]);

  if (!order) return null;

  return (
    <Modal
      title={`Завершить заявку #${order.id}`}
      open={isOpen}
      onCancel={handleCancel}
      width={500}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Отмена
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          disabled={!uploadedFileUuid}
        >
          Завершить заявку
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <p>
          <strong>Тип работы:</strong>
          {' '}
          {order.workType}
        </p>
        <p>
          <strong>Комментарий:</strong>
          {' '}
          {order.comment || 'Не указан'}
        </p>
      </div>

      <div>
        <p style={{ marginBottom: 8 }}>
          <strong>Приложите файл отчета (обязательно):</strong>
        </p>
        <FileUpload
          fileType="REPORT"
          maxCount={1}
          accept=".pdf,.doc,.docx,.xls,.xlsx"
          buttonText="Выберите файл отчета"
          onFileUploaded={handleFileUploaded}
          onFileRemoved={handleFileRemoved}
        />
        <p style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
          Поддерживаемые форматы: PDF, DOC, DOCX, XLS, XLSX
        </p>
      </div>
    </Modal>
  );
});

CompleteOrderModal.displayName = 'CompleteOrderModal';
